import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Gift, Check, Sparkles, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { fetchGift, claimGift } from "@/lib/api";
import { toast } from "sonner";

const GiftClaim = () => {
  const { code } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | claimed | notfound | done
  const [gift, setGift] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let ok = true;
    fetchGift(code)
      .then((g) => {
        if (!ok) return;
        setGift(g);
        setState(g.claimed ? "claimed" : "ready");
      })
      .catch(() => ok && setState("notfound"));
    return () => { ok = false; };
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please add your email so we can activate access.");
      return;
    }
    setSubmitting(true);
    try {
      await claimGift(code, { email: form.email.trim(), name: form.name.trim() || null });
      setState("done");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Unable to claim this gift.";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-cream pt-32 pb-24" data-testid="gift-claim-page">
      <div className="max-w-2xl mx-auto px-6">
        {state === "loading" && (
          <div className="text-center py-24" data-testid="gift-loading">
            <Loader2 className="w-10 h-10 text-emerald mx-auto animate-spin" />
            <p className="font-editorial italic text-2xl text-emerald mt-6">Looking up your gift…</p>
          </div>
        )}

        {state === "notfound" && (
          <div className="text-center py-16" data-testid="gift-notfound">
            <h1 className="font-display text-4xl text-emerald mb-4">We couldn't find that gift.</h1>
            <p className="font-body text-ink/60">
              Please double-check the code (letters and dashes) or ask the sender to forward the certificate.
              If payment was just made, it may take a moment to appear.
            </p>
          </div>
        )}

        {(state === "ready" || state === "claimed" || state === "done") && gift && (
          <Reveal>
            <p className="eyebrow text-emerald mb-4 flex items-center gap-2">
              <Gift className="w-4 h-4" /> A gift for you
            </p>
            <h1 className="font-display text-5xl md:text-6xl text-emerald leading-tight mb-6">
              {gift.sender_name} sent you<br />
              <span className="italic text-gold">{gift.months} month{gift.months !== 1 ? "s" : ""} of Academy.</span>
            </h1>

            {gift.personal_message && (
              <p className="font-editorial italic text-2xl text-emerald border-l-3 border-gold pl-6 my-8"
                 style={{ borderLeftWidth: 3 }}>
                &ldquo;{gift.personal_message}&rdquo;
              </p>
            )}

            <div className="mt-8 p-6 border border-line bg-surface">
              <p className="eyebrow text-emerald mb-3">Your gift includes</p>
              <ul className="space-y-2 font-body text-sm text-ink/80">
                <li className="flex gap-3"><Check className="w-4 h-4 text-emerald mt-1 shrink-0" />
                  {gift.months} month{gift.months !== 1 ? "s" : ""} of {gift.plan_name} Academy access</li>
                <li className="flex gap-3"><Check className="w-4 h-4 text-emerald mt-1 shrink-0" />
                  Live monthly Q&amp;A and masterclass invites</li>
                <li className="flex gap-3"><Check className="w-4 h-4 text-emerald mt-1 shrink-0" />
                  Downloadable worksheets, prompts, and assessments</li>
                <li className="flex gap-3"><Check className="w-4 h-4 text-emerald mt-1 shrink-0" />
                  Access to the member-only community</li>
              </ul>
            </div>

            <div className="mt-8 p-6 bg-emerald text-cream flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="eyebrow text-gold mb-1">Claim Code</p>
                <p className="font-mono text-2xl tracking-widest">{gift.gift_code}</p>
              </div>
              <Sparkles className="w-8 h-8 text-gold" />
            </div>

            {state === "ready" && (
              <form onSubmit={submit} className="mt-10" data-testid="gift-claim-form">
                <p className="eyebrow text-emerald mb-4">Activate your Academy access</p>
                <div className="space-y-3">
                  <input
                    data-testid="gift-claim-name"
                    className="input"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                  <input
                    data-testid="gift-claim-email"
                    type="email"
                    className="input"
                    placeholder="Your email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-emerald w-full justify-center mt-4 disabled:opacity-60"
                  data-testid="gift-claim-submit"
                >
                  {submitting ? "Activating…" : "Claim my gift"}
                </button>
                <p className="text-[11px] text-ink/45 font-body mt-3 text-center">
                  We'll email you on launch day with your login details and welcome kit.
                </p>
              </form>
            )}

            {state === "claimed" && (
              <div className="mt-10 p-6 border border-gold bg-gold/10" data-testid="gift-already-claimed">
                <p className="eyebrow text-emerald mb-2">Already claimed</p>
                <p className="font-body text-ink/80">
                  This gift has already been activated. If that wasn't you, please contact the sender.
                </p>
              </div>
            )}

            {state === "done" && (
              <div className="mt-10 p-6 bg-emerald text-cream" data-testid="gift-claim-success">
                <p className="eyebrow text-gold mb-2 flex items-center gap-2">
                  <Check className="w-4 h-4" /> Gift activated
                </p>
                <p className="font-body">
                  Beautiful. Watch your inbox on launch day for your Academy welcome kit and login.
                </p>
              </div>
            )}
          </Reveal>
        )}
      </div>
    </main>
  );
};

export default GiftClaim;
